import { gql } from '@apollo/client'

import fragments from './fragments'

export const adminQuoteModelRevisionQuery = gql`
    fragment AdminQuoteModelRevisionDataPartRevision on Revision {
        id
        modelFileName
        downloadUri
        cleanDownloadUri
        thumbnailUri
        sceneBundle
        units
        boundingBox {
            x
            y
            z
        }
        rawAnalysis
        partId
        updatedAt
    }

    fragment AdminQuoteModelRevisionHole on Hole {
        id
        groupKey
        type
        depth
        diameter
        isBlind
        tipAngle
        counterboreDepth
        counterboreDiameter
        countersinkAngle
        countersinkDepth
        countersinkDiameter
        direction
        endPoint
        startPoint
        tapOptions {
            id
            maxDepth
            commonName
        }
        tapAnnotation {
            id
            depth
            tapOption {
                id
                maxDepth
                commonName
            }
        }
    }

    fragment AdminQuoteModelRevisionSanitizedAttachment on SanitizedAttachment {
        id
        downloadUrl
    }

    fragment AdminQuoteModelRevisionGeneratedDrawing on GeneratedDrawing {
        id
        attachment {
            id
            downloadUrl
        }
    }

    fragment AdminQuoteModelRevisionQuoteModelQuoteUser on BasicUser {
        id
        firstName
        lastName
        email
    }

    fragment AdminQuoteModelRevisionQuoteModelQuote on Quote {
        id
        name
        status
        isFlexiblePriceQuote
        openEndedQuoteInviteUrl
        isCpqOwned @include(if: $isCpqFlagV2Enabled)
        type
        user {
            ...AdminQuoteModelRevisionQuoteModelQuoteUser
        }
        quoteModels(limit: 1000) {
            items {
                id
                selectedQuoteModelRevision {
                    processesDetails {
                        totalPrice {
                            ... on ExactPrice {
                                unitPriceInCents
                                priceInCents
                            }
                            ... on EstimatedPrice {
                                lowPriceInCents
                                lowUnitPriceInCents
                                highPriceInCents
                                highUnitPriceInCents
                            }
                        }
                    }
                }
                quoteModelRevisions {
                    id
                    dfmFeedback(includeDraft: true) {
                        isPublished
                    }
                    dfmReports {
                        isPublished
                        source
                        uploaderEmail
                        asset {
                            id
                            fileName
                            createdAt
                            downloadUrl
                        }
                    }
                    partRevision {
                        modelFileName
                        thumbnailUri
                    }
                    quoteModel {
                        quote {
                            isQuotePurchasable
                        }
                    }
                }
            }
        }
        quoteAttachments {
            items {
                id
                latestAttachmentRevision {
                    id
                    attachment {
                        ...adminAttachment
                    }
                }
            }
        }
    }

    fragment AdminQuoteModelRevisionDfmReport on PartDfmReport {
        quoteAttachmentRevisionId
        asset {
            id
            createdAt
            downloadUrl
            fileName
        }
        uploaderEmail
        source
        isPublished
    }

    fragment AdminQuoteModelRevisionData on QuoteModelRevision {
        id
        isITARProhibitedViewing
        isExclusiveToSingleQuote
        canBeExclusiveToSingleQuote
        fileName
        version
        updatedAt
        flags
        state
        supportedPriorityRegions {
            partSupportsDomestic
            partSupportsInternational
        }
        dfmReports {
            ...AdminQuoteModelRevisionDfmReport
        }
        quoteModel {
            id
            quote {
                ...AdminQuoteModelRevisionQuoteModelQuote
            }
        }
        sourceQuoteModelRevisionId
        annotations {
            id
        }
        manufacturingDrawing {
            ... on SanitizedAttachment {
                ...AdminQuoteModelRevisionSanitizedAttachment
            }
            ... on GeneratedDrawing {
                ...AdminQuoteModelRevisionGeneratedDrawing
            }
        }
        attachmentLink {
            id
            quoteAttachmentRevision {
                id
                quoteAttachment {
                    id
                }
                attachment {
                    ... on Attachment {
                        id
                        fileName
                        downloadUrl
                        sanitized {
                            id
                            downloadUrl
                            fileName
                        }
                        annotated {
                            id
                            downloadUrl
                            fileName
                        }
                    }
                }
            }
            createdAt
            isPdfOcrAutoQuotable
        }
        holes {
            ...AdminQuoteModelRevisionHole
        }
        taps {
            id
        }
        partRevision {
            ...AdminQuoteModelRevisionDataPartRevision
        }
        dfmFeedback(includeDraft: true) {
            ...dfmFeedbackDeprecated
        }
        quantityRangeStatus
        processesDetails {
            ...processesDetailsDeprecated
        }
        modelConfiguration {
            ...adminModelConfigurationDeprecated
        }
        mold {
            id
            inspectionData
            price {
                priceInCents
                unitPriceInCents
            }
            manualOverrideMpUnitPayout
            manualOverrideUnitAmount
        }
    }

    query adminQuoteModelRevision(
        $id: UUID!
        $showDeleted: Boolean
        $isCpqFlagV2Enabled: Boolean = false
    ) {
        quoteModelRevision(id: $id, showDeleted: $showDeleted) {
            ...AdminQuoteModelRevisionData
        }
    }
    ${fragments.Attachment}
    ${fragments.DfmFeedbackDeprecated}
    ${fragments.ProcessesDetailsDeprecated}
    ${fragments.ModelConfigurationDeprecated}
`
